/**
 * Protected-branch partition (#801). A delete or move of a folder skips each direct
 * branch holding something the actor may not change; the storage chokepoints refuse
 * a WHOLE-subtree delete/move that would carry one away. Matched by NAME in
 * `apps/api/src/middleware/error.ts` and the refusal recognisers (sync, copy executors).
 */
export const PROTECTED_BRANCHES_CODE = 'PROTECTED_BRANCHES' as const

export class ProtectedBranchError extends Error {
  readonly code = PROTECTED_BRANCHES_CODE
  /** E1: the root is an app/project — refused whole, never split. */
  readonly entityRoot?: boolean
  /** E2/E5: a split would leave the root's own access setup behind; the mover may not edit it. */
  readonly needsManage?: boolean
  /** E8: the split would leave the mover unable to manage the new shell (#787). */
  readonly wouldLoseAccess?: boolean
  constructor(opts?: { entityRoot?: boolean; needsManage?: boolean; wouldLoseAccess?: boolean; message?: string }) {
    super(opts?.message ?? "This folder contains items you can't change.")
    if (opts?.entityRoot) this.entityRoot = true
    if (opts?.needsManage) this.needsManage = true
    if (opts?.wouldLoseAccess) this.wouldLoseAccess = true
    this.name = 'ProtectedBranchError'
  }
}

/** [R14] The split-move shell's access copy was refused by the ONE grant judge (judgeGrantChange). */
export class ShellGovernanceError extends Error {
  readonly code = 'SHELL_GOVERNANCE' as const
  constructor(readonly refusal: { code: string; error: string }) {
    super(refusal.error)
    this.name = 'ShellGovernanceError'
  }
}

/** The subtree changed between the preview/prewarm and the act. Retryable. */
export class PartitionChangedError extends Error {
  readonly code = 'PARTITION_CHANGED' as const
  constructor() {
    super('This folder changed while we were working on it. Try again.')
    this.name = 'PartitionChangedError'
  }
}

/**
 * A split move's shell name is already taken at the destination by an unrelated item —
 * an ordinary name conflict (the same 409 `conflict` answer a whole move gives), never a
 * "changed, try again": re-previewing cannot clear it. `PartitionChangedError` stays for
 * the race only (the clashing folder holds branches of this very move, or the name was
 * taken between the check and the create).
 */
export class SplitShellNameTakenError extends Error {
  readonly code = 'NAME_CONFLICT' as const
  readonly conflict = true as const
  constructor() {
    super('Name conflict')
    this.name = 'SplitShellNameTakenError'
  }
}

/** The walk hit its depth cap with more below — never partition what we can't see. */
export class SubtreeTooDeepError extends Error {
  readonly code = 'SUBTREE_TOO_DEEP' as const
  constructor() {
    super('This folder is nested too deeply to delete or move in one step.')
    this.name = 'SubtreeTooDeepError'
  }
}

/** The ONLY wire shape of a partition — a branch the surface does not list is a count. */
export interface PartitionPreview {
  protected: Array<{ id: string; name: string; isFolder: boolean; reason: 'self' | 'descendant' }>
  hiddenProtectedCount: number
  removable: Array<{ id: string; name: string; isFolder: boolean }>
  entityRoot: boolean
  rootAccess: null | { restricted: boolean; grantCount: number; linkCount: number; orgShareCount: number; appGrantCount: number; canManage: boolean }
  /**
   * #801 L4 round 6 (M-17c) — present only on a MOVE preview asked WITH its destination
   * (`?dest=`): the move to that destination WILL be refused, and why. The server ran the
   * move's own judgements (the destination write gate, the access-change guard's hard
   * refusal, the split's E5 / E8 / shell-governance checks as a rolled-back dry run). A
   * client shows a refusal — never a confirm that will fail. Absent ⇒ not refused up front
   * (an access-change CONFIRMATION prompt, a name clash etc. still come from the act).
   */
  blocked?: PartitionMoveBlock
}

/** Why a previewed move will be refused (see `PartitionPreview.blocked`). */
export type PartitionMoveBlock =
  | 'destNotWritable' | 'destUnavailable' | 'nestedShared' | 'accessChange' | 'needsManage' | 'wouldLoseAccess' | 'shellGovernance'

/**
 * Does a move preview's `rootAccess` refuse the SPLIT? A split leaves the root in
 * place and carries its own access setup (Restricted, explicit grants, share links,
 * org shares) onto the new shell — so a mover who can't manage the root's access is
 * refused (409 `PROTECTED_BRANCHES` + `needsManage`, the server's
 * `ownAccess && !mayChangeRootAccess` in `TreeStorage.moveSplitting`). App grants
 * stay with the app and are not own access. The ONE client reading of it: the
 * preview component and every move caller ask this, never a hand copy.
 * `null`/absent (a delete preview) never blocks.
 */
export function rootAccessBlocksSplit(rootAccess: PartitionPreview['rootAccess'] | undefined): boolean {
  return !!rootAccess && !rootAccess.canManage && (
    rootAccess.restricted || rootAccess.grantCount > 0 || rootAccess.linkCount > 0 || rootAccess.orgShareCount > 0
  )
}
